/**
 * Tools → Integrations: Gmail, then QuickBooks.
 *
 * Two integrations with different audiences, which is why they are two
 * components rather than one. Connecting Gmail is per person — every rep needs
 * their own, so a customer's reply reaches whoever sent the estimate — and the
 * books are an admin's to connect. The admin gate therefore sits inside the
 * QuickBooks half rather than over the screen, or a rep would be locked out of
 * the one thing here that is theirs.
 *
 * QuickBooks (docs/adr/0038) has two jobs, in the order somebody needs them:
 *
 * 1. Connect, and say plainly which company file is connected. Connecting to
 *    the wrong company is the mistake that is expensive to find later, so the
 *    company's name is shown back rather than a green tick.
 * 2. Send what is waiting, and say what happened — including how many were
 *    refused.
 *
 * There was a third: two tables listing what had not gone, one for invoices and
 * one for job costs. Jonathon asked for both on 2026-09-07 (ADR 0112). The
 * refusals are still counted in the sentence the button writes; nothing else
 * shows them, and ADR 0112 says why that is a real cost rather than a tidy-up.
 *
 * The tokens are never here. Connecting redirects to Intuit and hands the code
 * back to the `quickbooks` Edge Function, which is the only thing that ever
 * holds a token (CLAUDE.md rule 7).
 */

import { formatMoney, toCents } from "@roof-hawk/core";
import { useCallback, useEffect, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { GmailAccounts } from "./GmailAccounts";
import { GoogleContactsSync } from "./GoogleContactsSync";
import { MercuryPayments } from "./MercuryPayments";
import { ReceiptInbox } from "./ReceiptInbox";
import { fmtDateOnly } from "../../lib/format";
import {
  fetchQuickbooksQueue,
  fetchQuickbooksStatus,
  recordBillAmounts,
  type QuickbooksInvoiceRow,
} from "../../lib/queries";
import { supabase } from "../../lib/supabase";
import { useSession } from "../../session";

/** Where Intuit sends the browser back to, and where the nonce is kept. */
const RETURN_PATH = "/tools/quickbooks";
const STATE_KEY = "quickbooks_oauth_state";

/**
 * Whether this page load is Intuit returning from the consent screen.
 *
 * The app has no router — every path but `/p/<token>` renders the shell — so
 * two places have to agree that this one opens Tools → Integrations rather than
 * the usual landing screen: `Shell` picks the tab, `Tools` picks the section,
 * and if either forgets, the single-use code is dropped and the connection
 * silently never happens. One function, so they cannot disagree.
 */
export function isQuickbooksReturn(pathname: string): boolean {
  return (
    pathname === RETURN_PATH ||
    pathname === `${RETURN_PATH}/` ||
    pathname.startsWith(`${RETURN_PATH}?`)
  );
}

/**
 * What a press of Send now says, out of what the push answered.
 *
 * `billsError` is the push saying it could not read the cost queue at all —
 * not that nothing was waiting (ADR 0276). It is shown as an error, beside
 * whatever the invoices did, because a cost queue that cannot be read stops
 * every cost bill until somebody fixes it.
 */
export function pushOutcome(result: Record<string, unknown>): {
  note: string;
  error: string | null;
} {
  const sent = Number(result.sent ?? 0);
  const failed = Number(result.failed ?? 0);
  const costsSent = Number(result.billsSent ?? 0);
  const costsFailed = Number(result.billsFailed ?? 0);
  const error =
    typeof result.billsError === "string" && result.billsError !== ""
      ? result.billsError
      : null;

  const parts: string[] = [];
  if (sent > 0) parts.push(tn(sent, "1 invoice", "{n} invoices"));
  if (costsSent > 0) parts.push(tn(costsSent, "1 cost", "{n} costs"));
  const refused = failed + costsFailed;
  const what = parts.join(` ${t("and")} `);
  const note =
    refused > 0
      ? t("{what} sent, {n} refused — see below.", {
          what: what || t("Nothing"),
          n: refused,
        })
      : parts.length > 0
        ? t("{what} sent to QuickBooks.", { what })
        : error
          ? t("No costs were sent — see the error above.")
          : t("Nothing was waiting.");
  return { note, error };
}

interface Status {
  connected: boolean;
  company_name: string | null;
  realm_id: string | null;
  connected_at: string | null;
}

async function callFunction(
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke("quickbooks", {
    body,
  });
  if (error) {
    // The function returns its own message in the body on a 4xx; surface that
    // rather than "Edge Function returned a non-2xx status code", which tells
    // nobody anything.
    const detail = (data as { error?: string } | null)?.error;
    throw new Error(
      detail ??
        t(
          "Could not reach the QuickBooks connection. It may not be deployed yet — Actions → Deploy to production, with 'quickbooks' in the functions box.",
        ),
    );
  }
  const payload = (data ?? {}) as Record<string, unknown>;
  if (typeof payload.error === "string") throw new Error(payload.error);
  return payload;
}

function QuickbooksIntegration() {
  const { isAdmin } = useSession();
  const [status, setStatus] = useState<Status | null>(null);
  const [queue, setQueue] = useState<QuickbooksInvoiceRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [next, rows] = await Promise.all([
      fetchQuickbooksStatus(),
      fetchQuickbooksQueue(),
    ]);
    setStatus(next);
    setQueue(rows);
  }, []);

  /**
   * Intuit sends the browser back here with `?code=…&realmId=…`. The code is
   * single-use and short-lived, and it is exchanged by the Edge Function under
   * this admin's own session — the browser never sees a token.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const realmId = params.get("realmId");
    const returnedState = params.get("state");
    if (!code || !realmId) {
      void refresh().catch((e) => setError(String(e.message ?? e)));
      return;
    }

    const expected = window.sessionStorage.getItem(STATE_KEY);
    window.sessionStorage.removeItem(STATE_KEY);
    window.history.replaceState({}, "", RETURN_PATH);

    if (!expected || expected !== returnedState) {
      setError(
        t(
          "That connection attempt did not start here, so it was refused. Press Connect to QuickBooks and follow it through in this tab.",
        ),
      );
      void refresh().catch(() => {});
      return;
    }

    setBusy(true);
    callFunction({ action: "connect", code, realmId })
      .then(() => setNote(t("Connected.")))
      .catch((e) => setError(String(e.message ?? e)))
      .finally(() => {
        setBusy(false);
        void refresh().catch(() => {});
      });
  }, [refresh]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await work();
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function connect() {
    // A nonce, so a code arriving from anywhere else is refused.
    const state = crypto.randomUUID();
    window.sessionStorage.setItem(STATE_KEY, state);
    const result = await callFunction({ action: "authorize", state });
    window.location.href = String(result.url);
  }

  async function disconnect() {
    if (
      !window.confirm(
        t(
          "Disconnect QuickBooks? Nothing already in QuickBooks changes, but no new invoice will reach it until it is connected again.",
        ),
      )
    )
      return;
    await callFunction({ action: "disconnect" });
    await refresh();
    setNote(t("Disconnected."));
  }

  async function push() {
    // Price whatever costs are queued before pushing, so one press sends both
    // halves of the books rather than only the invoices (ADR 0088).
    await recordBillAmounts();
    const result = await callFunction({ action: "push" });
    await refresh();
    const outcome = pushOutcome(result);
    if (outcome.error) setError(outcome.error);
    setNote(outcome.note);
  }

  if (!isAdmin) {
    return (
      <div className="shelf">
        <b>{t("Admins only")}</b>
        {t("The books are an admin's to connect.")}
      </div>
    );
  }

  const waiting = queue.filter((row) => row.state === "pending");

  return (
    <>
      {error && <ErrorNote error={error} />}
      {note && <div className="shelf">{note}</div>}

      <div className="shelf">
        <b>QuickBooks</b>
        {status?.connected ? (
          <>
            <div>
              {t("Connected to")} <b>{status.company_name ?? "QuickBooks"}</b>
              {status.connected_at
                ? " " +
                  t("since {date}", { date: fmtDateOnly(status.connected_at) })
                : ""}
              .
            </div>
            <div className="muted">
              {t(
                "A job's invoice is sent when its handoff is pressed — when the job is sold, not when an estimate is sent. Send now is for anything that did not go first time.",
              )}
            </div>
            <div className="row">
              <button disabled={busy} onClick={() => void run(push)}>
                {waiting.length > 0
                  ? t("Send {n} waiting", { n: waiting.length })
                  : t("Send now")}
              </button>
              <button disabled={busy} onClick={() => void run(disconnect)}>
                {t("Disconnect")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div>{t("Not connected. Nothing is reaching the accounts.")}</div>
            <div className="row">
              <button disabled={busy} onClick={() => void run(connect)}>
                {t("Connect to QuickBooks")}
              </button>
            </div>
          </>
        )}
      </div>

      {waiting.length > 0 && (
        <div className="block">
          <h3>{t("Invoices waiting to go")}</h3>
          <div className="muted">
            {t(
              "Sold jobs whose invoice has not been sent to QuickBooks yet. Press Send above and they go.",
            )}
          </div>
          <table className="grid">
            <thead>
              <tr>
                <th>{t("Job")}</th>
                <th>{t("Sold")}</th>
                <th>{t("Amount")}</th>
              </tr>
            </thead>
            <tbody>
              {waiting.map((row) => (
                <tr key={row.id}>
                  <td>{row.job?.job_number ?? "—"}</td>
                  <td>{fmtDateOnly(row.sold_at)}</td>
                  <td>
                    {row.amount_cents == null ? (
                      <span className="muted">
                        {t("No contract amount worked out yet")}
                      </span>
                    ) : (
                      formatMoney(toCents(row.amount_cents))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ReceiptInbox />
    </>
  );
}

/**
 * The screen itself. Gmail first: it is the one everybody has to do, and the
 * one a rep came here for.
 */
export function IntegrationsTool() {
  return (
    <>
      <GmailAccounts />
      <GoogleContactsSync />
      <QuickbooksIntegration />
      <MercuryPayments />
    </>
  );
}
